"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarRange, ChartLine as LineChart, ShieldAlert, ArrowUpRight, Compass, Sun, CloudRain } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const challenges = [{
  id: 1,
  title: "Seasonality & Delhi-NCR Weekend Surges",
  subtitle: "Demand management",
  iconName: "CalendarRange",
  description: "Manage sharp weekend spikes from North Indian metro markets alongside prolonged monsoon dips and mid-week lulls. We build dynamic minimum length-of-stay controls and pace-based pricing to prevent off-peak panic discounting.",
  metricLabel: "Strategy focus",
  metricValue: "Pace-based pricing, MLOS restrictions & mid-week corporate packages"
}, {
  id: 2,
  title: "Aggressive Comp-Set Undercutting",
  subtitle: "Real-time rate intelligence",
  iconName: "LineChart",
  description: "Track intraday rate movements across Tapovan, Laxman Jhula, and luxury Ganges riverfronts on MakeMyTrip, Agoda, and Booking.com. Automated competitive alerts protect your RevPAR while securing high-yield bookings.",
  metricLabel: "Strategy focus",
  metricValue: "Intraday OTA tracking & dynamic room-tier rate fences"
}, {
  id: 3,
  title: "The OTA Commission & Parity Trap",
  subtitle: "Channel distribution",
  iconName: "ShieldAlert",
  description: "Eliminate unauthorized wholesaler inventory leakage and reclaim high margins lost to steep OTA commissions. We implement strict rate parity across metasearch and convert yoga, wellness, and adventure travelers directly.",
  metricLabel: "Strategy focus",
  metricValue: "Direct booking incentives & multi-channel rate parity enforcement"
}];
const seasonalitySeasons = [{
  period: "October – March",
  title: "Peak Spiritual & Wellness Season",
  icon: Compass,
  badgeText: "High Demand",
  badgeVariant: "default" as const,
  description: "International yoga practitioners, luxury wellness seekers, and pleasant weather drive maximum occupancy. Premium ADR fences, advance booking windows, and strict non-refundable policies maximize total yield.",
  focus: "Yield maximization & advance booking fences"
}, {
  period: "April – June",
  title: "Summer Adventure & Rafting Rush",
  icon: Sun,
  badgeText: "Weekend Spike",
  badgeVariant: "secondary" as const,
  description: "Heavy domestic demand driven by river rafting, corporate offsites, and weekend getaways from Delhi-NCR. Dynamic weekend vs. weekday tiering captures top RevPAR while stabilizing mid-week inventory.",
  focus: "Dynamic weekend premiums & minimum stay controls"
}, {
  period: "July – September",
  title: "Monsoon & Low-Season Yielding",
  icon: CloudRain,
  badgeText: "Value Capture",
  badgeVariant: "outline" as const,
  description: "River activity pauses and monsoon arrives. Strategy pivots toward long-stay yoga teacher training retreats, ayurvedic wellness packages, and localized leisure escapes to preserve base revenue without rate erosion.",
  focus: "Extended-stay packages & wellness niche targeting"
}];
export default function Landscape() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hospitalitylandscaperishikesh" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center mb-16 md:mb-20">
          <Badge variant="outline" className="mb-4 border-secondary/40 text-secondary-foreground">
            Market Dynamics & Yield Protection
          </Badge>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Navigating the hospitality landscape of Rishikesh
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Rishikesh presents a distinct micro-market driven by luxury wellness retreats, adventure travel, international yoga practitioners, and extreme weekend surges from Delhi-NCR. Sunil Singh builds disciplined, data-driven revenue systems tailored specifically for boutique riverside resorts, ashrams, and experiential hotels.
          </p>
        </div>

        {/* 3-Column Card Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {challenges.map((item, index) => <motion.div key={item.id} data-index={index} variants={cardVariants} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-300">
                <CardHeader className="gap-3">
                  <div className="flex items-center justify-between">
                    <div className="inline-flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary-foreground">
                      {item.iconName === "CalendarRange" && <CalendarRange className="size-6 text-chart-2" />}
                      {item.iconName === "LineChart" && <LineChart className="size-6 text-chart-2" />}
                      {item.iconName === "ShieldAlert" && <ShieldAlert className="size-6 text-chart-2" />}
                    </div>
                    <span className="text-xs font-mono font-medium text-muted-foreground tracking-wider uppercase">
                      0{index + 1}
                    </span>
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-secondary">
                      {item.subtitle}
                    </span>
                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold mt-1.5 text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="space-y-6 pt-2">
                  <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                    {item.description}
                  </CardDescription>

                  <div className="rounded-xl border border-border/60 bg-muted/40 p-3.5 text-xs">
                    <p className="font-semibold text-foreground mb-1">
                      {item.metricLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {item.metricValue}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Seasonality Block */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="mt-16 md:mt-20 rounded-xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-6 border-b border-border">
            <div>
              <span className="text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                Demand Cycles & Seasonality
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl font-bold mt-1 text-foreground">
                Rishikesh Seasonal Revenue Matrix
              </h3>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              A strategic roadmap for navigating peak pilgrimage periods, rafting seasons, and monsoon wellness occupancy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-8">
            {seasonalitySeasons.map((season, idx) => {
            const IconComp = season.icon;
            return <div key={season.period} data-index={idx} className="flex flex-col justify-between rounded-lg border border-border/70 bg-background/50 p-5 hover:border-secondary/50 transition-[border-color,box-shadow] duration-200">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="inline-flex size-10 items-center justify-center rounded-md bg-secondary/10 text-secondary">
                        <IconComp className="size-5" />
                      </div>
                      <Badge variant={season.badgeVariant} className="text-xs">
                        {season.badgeText}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs font-mono font-medium text-muted-foreground">
                        {season.period}
                      </p>
                      <h4 className="font-serif text-lg font-bold text-foreground mt-0.5">
                        {season.title}
                      </h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {season.description}
                    </p>
                  </div>
                  <div className="mt-4 pt-3 border-t border-border/50 text-xs">
                    <span className="font-medium text-secondary">Yield Action: </span>
                    <span className="text-foreground">{season.focus}</span>
                  </div>
                </div>;
          })}
          </div>
        </motion.div>

        {/* Section Bottom CTA / Footnote */}
        <div className="mt-14 md:mt-18 pt-8 border-t border-border/70 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Need a tailored revenue assessment for your Rishikesh hotel or boutique stay?
          </p>
          <Button asChild variant="default" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="#" className="inline-flex items-center gap-2">
              <span>Book a Revenue Audit</span>
              <ArrowUpRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}